import { ErrorBoundary } from "@prismicio/editor-ui"
import { type RefObject, Suspense } from "react"

import type { MediaAssetType } from "../MediaLibrary/hooks/mediaLibraryData"
import { EmptyMediaList } from "./components/EmptyMediaList"
import { MediaLibraryActions } from "./components/MediaLibraryActions"
import {
  MediaLibraryProvider,
  useMediaLibrary
} from "./components/MediaLibraryContext"
import { MediaLibraryDropzone } from "./components/MediaLibraryDropzone"
import { MediaLibraryRenderError } from "./components/MediaLibraryRenderError"
import { MediaLibrarySkeleton } from "./components/MediaLibrarySkeleton"
import { MediaLibraryToastProvider } from "./components/MediaLibraryToast"
import { MediaLibraryToolbar } from "./components/MediaLibraryToolbar"
import { MediaList } from "./components/MediaList"
import { Sidepane } from "./components/Sidepane"
import { MediaLibraryLayout } from "./MediaLibraryLayout"

interface MediaLibraryProps {
  /**
   * The type of media to fetch from the media library.
   * @default all
   */
  assetType?: MediaAssetType
  /**
   *  A ref to render the actions container outside the media library.
   */
  actionContainerRef?: RefObject<HTMLDivElement>
  onSelectedChange?: (ids: Set<string>) => void
}

export function MediaLibrary(props: MediaLibraryProps) {
  const { assetType, onSelectedChange, actionContainerRef } = props

  return (
    <ErrorBoundary renderError={() => <MediaLibraryRenderError />}>
      <Suspense
        fallback={
          <MediaLibrarySkeleton actionContainerRef={actionContainerRef} />
        }
      >
        <MediaLibraryProvider
          assetType={assetType}
          actionContainerRef={actionContainerRef}
          onSelectedChange={onSelectedChange}
        >
          <MediaLibraryDropzone>
            <MediaLibraryContent />
          </MediaLibraryDropzone>
        </MediaLibraryProvider>
      </Suspense>
    </ErrorBoundary>
  )
}

function MediaLibraryContent() {
  const { media } = useMediaLibrary()

  return (
    <MediaLibraryToastProvider>
      <MediaLibraryLayout
        sidePane={<Sidepane />}
        mediaActions={<MediaLibraryActions />}
        mediaToolbar={<MediaLibraryToolbar />}
        mediaList={media.length === 0 ? <EmptyMediaList /> : <MediaList />}
      />
    </MediaLibraryToastProvider>
  )
}
